/**
 * weaponModLoadout.ts — which specific mod ITEMS are socketed on which weapon.
 *
 * One mod per type-slot (so a gun can carry at most one of each of the 6 types).
 * Each equipped record keeps the item id + its rarity (for the buff) + code (for the sprite).
 * Persisted to its own localStorage key so it can't corrupt the main save.
 */
import type { ModCode, ModRarity } from './weaponMods';
import { getArmedWeapons, disarm } from './weaponArmed';
import { removeModItems } from './starforgeInventory';

const KEY = 'goopzone_weapon_mods';
export interface EquippedMod { code: ModCode; itemId: string; rarity: ModRarity; }
type Loadout = Record<string, EquippedMod[]>;

let _cache: Loadout | null = null;
function all(): Loadout {
  if (_cache) return _cache;
  try {
    const s = localStorage.getItem(KEY);
    const v = s ? JSON.parse(s) : {};
    _cache = v && typeof v === 'object' && !Array.isArray(v) ? (v as Loadout) : {};
    // migrate old "ModCode[]" shape → reset (re-equip in the new item-based model)
    for (const k of Object.keys(_cache)) {
      const arr = _cache[k];
      if (!Array.isArray(arr) || (arr.length > 0 && typeof arr[0] === 'string')) _cache[k] = [];
    }
  } catch { _cache = {}; }
  return _cache!;
}
function save() { try { localStorage.setItem(KEY, JSON.stringify(all())); } catch { /* private mode */ } }

export function getEquipped(weaponId: string): EquippedMod[] { return all()[weaponId] ?? []; }
export function equippedCodes(weaponId: string): ModCode[] { return getEquipped(weaponId).map(e => e.code); }
export function isEquipped(weaponId: string, itemId: string): boolean {
  return getEquipped(weaponId).some(e => e.itemId === itemId);
}

/** Socket an item (replaces any existing mod of the same type). Returns the new loadout. */
export function equip(weaponId: string, e: EquippedMod): EquippedMod[] {
  const cur = getEquipped(weaponId).filter(x => x.code !== e.code);
  cur.push(e);
  all()[weaponId] = cur; save();
  return cur;
}
export function unequip(weaponId: string, itemId: string): EquippedMod[] {
  const cur = getEquipped(weaponId).filter(x => x.itemId !== itemId);
  all()[weaponId] = cur; save();
  return cur;
}
export function clearEquipped(weaponId: string): void { all()[weaponId] = []; save(); }

/**
 * EXTRACTION DEATH PENALTY (BETA 22.45) — when the player DIES without extracting,
 * the mods socketed on the guns they brought into the run are DESTROYED (the Tarkov
 * risk: a modded gun "enters your run with its mods at risk"). Strips every armed
 * weapon's socketed mods from the stash + un-arms it. Mods left in the backpack (not
 * socketed) are safe. Returns how many mod items were lost. Call ONCE on death only —
 * NOT on extraction (extracting keeps your kitted guns).
 */
export function loseSocketedModsOnDeath(): number {
  let lost = 0;
  try {
    for (const wid of getArmedWeapons()) {
      const eq = getEquipped(wid);
      if (!eq.length) continue;
      lost += removeModItems(eq.map(e => e.itemId));
      clearEquipped(wid);
      disarm(wid);
    }
  } catch { /* localStorage / private mode */ }
  return lost;
}
